import { catchError, firstValueFrom, from, map, mergeMap } from 'rxjs';
import { RECAPTCHA_ACTION } from '../../authentication/constant/recaptcha';
import Authorization from '../../authentication/models/authorization';
import { UpdateCustomerInterface } from '../interfaces/api-customer-interface';
import ErrorMapping from '../mappings/error.mapping';
import CustomerMapping from '../mappings/customer-mapping';
import Customer from '../models/customer';
import CustomerProvider from '../providers/customer-provider';
import { Ubigeo } from '../models/ubigeo';
import { Country } from '../models/country';
import SessionProvider from 'domains/core-base/authentication/providers/session-provider';

class CustomerRepository {
  private auth: Authorization = Authorization.getInstance();

  constructor(
    private customerMapping: CustomerMapping,
    private errorMapping: ErrorMapping,
    private sessionProvider: SessionProvider,
  ) {}

  async queryByTypeAndDocument(
    documentId: string,
    documentType: string,
  ): Promise<Customer> {
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        documentId,
        documentType,
        recaptcha: recaptchaToken,
        authorization: this.auth.token,
      })),
      mergeMap(CustomerProvider.queryByTypeAndDocument),
      map((result) =>
        this.customerMapping.formatGetCustomerResponse(result.response),
      ),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async update(data: UpdateCustomerInterface): Promise<any> {
    const source$ = from(this.auth.executeRecaptcha(RECAPTCHA_ACTION)).pipe(
      map((recaptchaToken) => ({
        customer: data,
        recaptcha: recaptchaToken,
        authorization: this.auth.token,
      })),
      mergeMap(CustomerProvider.update),
      map((result) =>
        this.customerMapping.formatGetCustomerResponse(result.response),
      ),
      catchError(this.errorMapping.throwError),
    );

    return firstValueFrom(source$);
  }

  async getUbigeos(): Promise<Ubigeo[]> {
    const source$ = this.sessionProvider.getToken().pipe(
      map((sessionToken) => sessionToken),
      map((authorization) => ({
        authorization,
      })),
      mergeMap(CustomerProvider.getUbigeos),
      map((result) =>
        this.customerMapping.formatUbigeoResponse(result.response),
      ),
      catchError(this.errorMapping.throwError),
    );
    return firstValueFrom(source$);
  }

  async getCountries(): Promise<Country[]> {
    const source$ = CustomerProvider.getCuntries().pipe(
      map((result) =>
        this.customerMapping.formatCountriesResponse(result.response),
      ),
      catchError(this.errorMapping.throwError),
    );
    return await firstValueFrom(source$);
  }
}

export const customerRepository = new CustomerRepository(
  new CustomerMapping(),
  new ErrorMapping(),
  new SessionProvider(),
);
